/**
 * Public program-application form for prospective founders (no auth required).
 * Collects applicant, company, and de-identified demographic fields plus an
 * optional pitch deck, then posts to submitApplication.
 *
 * Multi-select fields (product type, investment sources, use of funds,
 * race/ethnicity) are held as arrays in state and serialized to comma-joined
 * strings for the API. The form validates fully client-side before submitting.
 */
import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { submitApplication } from '../services/apiService';

const STAGES = [
  { value: 'idea', label: 'Idea Stage' },
  { value: 'mvp', label: 'MVP / Pre-revenue' },
  { value: 'revenue', label: 'Early Revenue' },
  { value: 'scaling', label: 'Scaling' },
];

const INDUSTRIES = [
  'AgTech', 'CleanTech', 'EdTech', 'FinTech', 'HealthTech', 'Real Estate',
  'Logistics / Supply Chain', 'Consumer Products', 'SaaS / Software',
  'Manufacturing', 'Food & Beverage', 'Other',
];

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming',
];

const COUNTIES = [
  'Albany', 'Columbia', 'Greene', 'Rensselaer', 'Saratoga',
  'Schenectady', 'Warren', 'Washington', 'Other',
];

const HOW_HEARD_OPTIONS = [
  'Email', 'Event', 'Social media', 'Personal referral', 'Online search', 'Other',
];

const PRODUCT_TYPES = [
  'App / Software / Platform', 'Hardware / Manufactured Product', 'ClimateTech',
  'B2B', 'B2C', 'I have a patent',
];

const INVESTMENT_SOURCES = [
  'Owner / founder', 'Sales revenue', 'Venture capital', 'Friends & family',
  'Loans', 'Grants', 'Angels', 'None', 'Other',
];

const USE_OF_FUNDS_OPTIONS = [
  'Product development / R&D', 'Technology buildout', 'Hiring / team expansion',
  'Sales and marketing', 'Customer acquisition', 'Operations', 'Regulatory / compliance',
  'Facilities / equipment', 'Working capital', 'Market expansion',
  'Strategic partnerships', 'IP / patents', 'Debt repayment',
];

const RACE_ETHNICITY_OPTIONS = [
  'American Indian or Alaska Native', 'Asian', 'Black or African American',
  'Native Hawaiian or Other Pacific Islander', 'White', 'Two or more races',
  'Prefer not to answer',
];

const AGE_RANGES = [
  '18–24', '25–34', '35–44', '45–54', '55–64', '65+', 'Prefer not to answer',
];

const HOUSEHOLD_INCOMES = [
  'Under $25,000', '$25,000–$49,999', '$50,000–$74,999',
  '$75,000–$99,999', '$100,000–$149,999', '$150,000 or more', 'Prefer not to answer',
];

/** True for a non-empty value that parses as a number >= 0. */
function isNonNegativeNumber(value) {
  const v = String(value ?? '').trim();
  if (!v) return false;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0;
}

/** Full program application form; renders a success screen once submitted. */
export default function Apply() {
  // Prefill email when arriving from an EIR's "invite to apply" link (?email=…).
  const invitedEmail = (() => {
    try { return new URLSearchParams(window.location.search).get('email') || ''; }
    catch { return ''; }
  })();
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: invitedEmail, password: '', confirmPassword: '',
    address: '', address2: '', city: '', state: '', zipCode: '', county: '',
    inCapitalRegion: '', jobTitle: '', phone: '', supportType: '', howHeard: '',
    companyName: '', companyStage: '', industry: '', description: '', website: '', noWebsite: false,
    hasRevenue: '', cashInvested: '', amountSeeking: '',
    interestedInInvestment: '', inOtherPrograms: '',
    productType: [], investmentSources: [], useOfFunds: [], raceEthnicity: [],
    totalHeadcount: '', capitalRegionHeadcount: '',
    isMinorityWomenOwned: '', isHispanicLatino: '', isVeteranOwned: '',
    ageRange: '', householdIncome: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState('');
  const [pitchDeck, setPitchDeck] = useState(null);
  const pitchDeckRef = useRef(null);

  function set(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  function setRadio(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function toggleCheckbox(field, value) {
    setForm((f) => {
      const arr = f[field];
      return {
        ...f,
        [field]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value],
      };
    });
  }

  // Returns a {field: message} map of required-field errors; empty means valid.
  function validate() {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = 'Required';
    if (!form.lastName.trim()) errs.lastName = 'Required';
    if (!form.email.trim()) errs.email = 'Required';
    if (!form.password) errs.password = 'Required';
    if (form.password && form.password.length < 8) errs.password = 'Must be at least 8 characters';
    if (form.password !== form.confirmPassword) errs.confirmPassword = 'Passwords do not match';
    if (!form.address.trim()) errs.address = 'Required';
    if (!form.city.trim()) errs.city = 'Required';
    if (!form.state) errs.state = 'Required';
    if (!form.zipCode.trim()) errs.zipCode = 'Required';
    if (!form.county) errs.county = 'Required';
    if (!form.inCapitalRegion) errs.inCapitalRegion = 'Required';
    if (!form.jobTitle.trim()) errs.jobTitle = 'Required';
    if (!form.phone.trim()) errs.phone = 'Required';
    if (!form.howHeard) errs.howHeard = 'Required';
    if (!form.supportType.trim()) errs.supportType = 'Required';
    if (!form.companyName.trim()) errs.companyName = 'Required';
    if (!form.companyStage) errs.companyStage = 'Required';
    if (!form.industry) errs.industry = 'Required';
    if (!form.description.trim()) errs.description = 'Required';
    // Required so the CRM can enrich from the domain — unless there is no site yet.
    if (!form.noWebsite) {
      if (!form.website.trim()) errs.website = 'Required — or tick the box below';
      else if (!/^https?:\/\/.+/i.test(form.website.trim())) errs.website = 'Must start with http:// or https://';
    }
    if (form.productType.length === 0) errs.productType = 'Select at least one';
    if (!form.hasRevenue) errs.hasRevenue = 'Required';
    if (!isNonNegativeNumber(form.cashInvested)) errs.cashInvested = 'Enter a number, e.g. 50000';
    if (form.investmentSources.length === 0) errs.investmentSources = 'Select at least one';
    if (!isNonNegativeNumber(form.amountSeeking)) errs.amountSeeking = 'Enter a number, e.g. 250000';
    if (!form.interestedInInvestment) errs.interestedInInvestment = 'Required';
    if (!form.inOtherPrograms) errs.inOtherPrograms = 'Required';
    if (!isNonNegativeNumber(form.totalHeadcount)) errs.totalHeadcount = 'Enter a number';
    if (!isNonNegativeNumber(form.capitalRegionHeadcount)) errs.capitalRegionHeadcount = 'Enter a number';
    if (!form.isMinorityWomenOwned) errs.isMinorityWomenOwned = 'Required';
    if (!form.isHispanicLatino) errs.isHispanicLatino = 'Required';
    if (form.raceEthnicity.length === 0) errs.raceEthnicity = 'Select at least one';
    if (!form.isVeteranOwned) errs.isVeteranOwned = 'Required';
    if (!form.ageRange) errs.ageRange = 'Required';
    if (!form.householdIncome) errs.householdIncome = 'Required';
    return errs;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    setServerError('');
    try {
      await submitApplication({
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        password: form.password,
        address: form.address,
        address2: form.address2,
        city: form.city,
        state: form.state,
        zip_code: form.zipCode,
        county: form.county,
        in_capital_region: form.inCapitalRegion,
        job_title: form.jobTitle,
        phone: form.phone,
        how_heard: form.howHeard,
        support_type: form.supportType,
        company_name: form.companyName,
        company_stage: form.companyStage,
        industry: form.industry,
        description: form.description,
        website: form.noWebsite ? '' : form.website,
        no_website: form.noWebsite,
        product_type: form.productType.join(','),
        has_revenue: form.hasRevenue,
        cash_invested: form.cashInvested,
        investment_sources: form.investmentSources.join(','),
        amount_seeking: form.amountSeeking,
        use_of_funds: form.useOfFunds.join(','),
        interested_in_investment: form.interestedInInvestment,
        in_other_programs: form.inOtherPrograms,
        total_headcount: form.totalHeadcount,
        capital_region_headcount: form.capitalRegionHeadcount,
        is_minority_women_owned: form.isMinorityWomenOwned,
        is_hispanic_latino: form.isHispanicLatino,
        race_ethnicity: form.raceEthnicity.join(','),
        is_veteran_owned: form.isVeteranOwned,
        age_range: form.ageRange,
        household_income: form.householdIncome,
      }, pitchDeck);
      setSubmitted(true);
    } catch (err) {
      setServerError(err.response?.data?.detail || err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div style={styles.page}>
        <div style={{ ...styles.card, maxWidth: 520 }}>
          <div style={styles.successIcon}>✓</div>
          <h1 style={styles.successTitle}>Application submitted</h1>
          <p style={styles.successText}>
            Thank you, {form.firstName}. We've received your application and will
            be in touch within a few business days. Once approved, you'll be able
            to sign in and access the portal.
          </p>
          <Link to="/login" style={styles.backLink}>← Back to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.title}>Apply to the Program</h1>
        <p style={styles.subtitle}>
          Tell us about yourself and your company. We review every application personally.
        </p>

        <form onSubmit={handleSubmit} noValidate>

          {/* ── OVERVIEW ──────────────────────────────── */}
          <SectionHeader>Overview</SectionHeader>

          <div style={styles.row2}>
            <Field label="First name *" error={errors.firstName}>
              <input style={inputStyle(errors.firstName)} value={form.firstName} onChange={set('firstName')} placeholder="John" />
            </Field>
            <Field label="Last name *" error={errors.lastName}>
              <input style={inputStyle(errors.lastName)} value={form.lastName} onChange={set('lastName')} placeholder="Smith" />
            </Field>
          </div>

          <div style={styles.row2}>
            <Field label="Street address *" error={errors.address}>
              <input style={inputStyle(errors.address)} value={form.address} onChange={set('address')} placeholder="123 Main St" />
            </Field>
            <Field label="Street address 2">
              <input style={inputStyle()} value={form.address2} onChange={set('address2')} placeholder="Apt, suite, unit (optional)" />
            </Field>
          </div>

          <div style={styles.row3}>
            <Field label="City *" error={errors.city}>
              <input style={inputStyle(errors.city)} value={form.city} onChange={set('city')} placeholder="Albany" />
            </Field>
            <Field label="State *" error={errors.state}>
              <select style={inputStyle(errors.state)} value={form.state} onChange={set('state')}>
                <option value="">State…</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Zip code *" error={errors.zipCode}>
              <input style={inputStyle(errors.zipCode)} value={form.zipCode} onChange={set('zipCode')} placeholder="12207" />
            </Field>
          </div>

          <RadioGroup
            label="Are you located in the Capital Region? *"
            error={errors.inCapitalRegion}
            options={['Yes', 'No']}
            value={form.inCapitalRegion}
            onChange={(v) => setRadio('inCapitalRegion', v)}
          />

          <div style={styles.row2}>
            <Field label="County *" error={errors.county}>
              <select style={inputStyle(errors.county)} value={form.county} onChange={set('county')}>
                <option value="">Select county…</option>
                {COUNTIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="How did you hear about us? *" error={errors.howHeard}>
              <select style={inputStyle(errors.howHeard)} value={form.howHeard} onChange={set('howHeard')}>
                <option value="">Select…</option>
                {HOW_HEARD_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
          </div>

          <div style={styles.row2}>
            <Field label="Company name *" error={errors.companyName}>
              <input style={inputStyle(errors.companyName)} value={form.companyName} onChange={set('companyName')} placeholder="Acme Corp" />
            </Field>
            <Field label="Job title *" error={errors.jobTitle}>
              <input style={inputStyle(errors.jobTitle)} value={form.jobTitle} onChange={set('jobTitle')} placeholder="CEO / Founder" />
            </Field>
          </div>

          <Field label="Website *" error={errors.website}>
            <input
              style={inputStyle(errors.website)}
              value={form.website}
              onChange={set('website')}
              placeholder="https://yourcompany.com"
              disabled={form.noWebsite}
            />
            <label style={styles.inlineCheck}>
              <input
                type="checkbox"
                checked={form.noWebsite}
                onChange={(e) => setForm((f) => ({
                  ...f,
                  noWebsite: e.target.checked,
                  website: e.target.checked ? '' : f.website,
                }))}
              />
              We don't have a website yet
            </label>
          </Field>

          <div style={styles.row2}>
            <Field label="Email *" error={errors.email}>
              <input type="email" style={inputStyle(errors.email)} value={form.email} onChange={set('email')} placeholder="you@company.com" />
            </Field>
            <Field label="Phone *" error={errors.phone}>
              <input type="tel" style={inputStyle(errors.phone)} value={form.phone} onChange={set('phone')} placeholder="(518) 555-0100" />
            </Field>
          </div>

          <div style={styles.row2}>
            <Field
              label="Password *"
              error={errors.password}
              action={<button type="button" style={styles.showHideBtn} onClick={() => setShowPassword((v) => !v)}>{showPassword ? 'Hide' : 'Show'}</button>}
            >
              <input type={showPassword ? 'text' : 'password'} style={inputStyle(errors.password)} value={form.password} onChange={set('password')} placeholder="Min. 8 characters" />
            </Field>
            <Field label="Confirm password *" error={errors.confirmPassword}>
              <input type={showPassword ? 'text' : 'password'} style={inputStyle(errors.confirmPassword)} value={form.confirmPassword} onChange={set('confirmPassword')} placeholder="Repeat password" />
            </Field>
          </div>

          <Field label="What type of support are you looking for? *" error={errors.supportType}>
            <textarea
              style={{ ...inputStyle(errors.supportType), resize: 'vertical', minHeight: 80 }}
              value={form.supportType}
              onChange={set('supportType')}
              placeholder="Describe the support or resources you're hoping to access through this program…"
            />
          </Field>

          {/* ── COMPANY DETAILS ───────────────────────── */}
          <SectionHeader>Company Details</SectionHeader>

          <div style={styles.row2}>
            <Field label="Sector *" error={errors.industry}>
              <select style={inputStyle(errors.industry)} value={form.industry} onChange={set('industry')}>
                <option value="">Select sector…</option>
                {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </Field>
            <Field label="Business stage *" error={errors.companyStage}>
              <select style={inputStyle(errors.companyStage)} value={form.companyStage} onChange={set('companyStage')}>
                <option value="">Select stage…</option>
                {STAGES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Value proposition *" error={errors.description}>
            <textarea
              style={{ ...inputStyle(errors.description), resize: 'vertical', minHeight: 90 }}
              value={form.description}
              onChange={set('description')}
              placeholder="What does your company do and what problem are you solving? (50 words or less)"
            />
          </Field>

          <CheckboxGroup
            label="Product type *"
            error={errors.productType}
            options={PRODUCT_TYPES}
            selected={form.productType}
            onToggle={(v) => toggleCheckbox('productType', v)}
          />

          <RadioGroup
            label="Does your company have revenue? *"
            error={errors.hasRevenue}
            options={['Yes', 'No']}
            value={form.hasRevenue}
            onChange={(v) => setRadio('hasRevenue', v)}
          />

          <Field label="Cash invested to date ($) *" error={errors.cashInvested}>
            <input type="number" min="0" step="any" style={inputStyle(errors.cashInvested)} value={form.cashInvested} onChange={set('cashInvested')} placeholder="50000" />
          </Field>

          <CheckboxGroup
            label="Investment sources *"
            error={errors.investmentSources}
            options={INVESTMENT_SOURCES}
            selected={form.investmentSources}
            onToggle={(v) => toggleCheckbox('investmentSources', v)}
          />

          <Field label="Amount seeking ($) *" error={errors.amountSeeking}>
            <input type="number" min="0" step="any" style={inputStyle(errors.amountSeeking)} value={form.amountSeeking} onChange={set('amountSeeking')} placeholder="250000" />
          </Field>

          <CheckboxGroup
            label="Use of funds (optional)"
            options={USE_OF_FUNDS_OPTIONS}
            selected={form.useOfFunds}
            onToggle={(v) => toggleCheckbox('useOfFunds', v)}
          />

          <RadioGroup
            label="Interested in outside investment? *"
            error={errors.interestedInInvestment}
            options={['Yes', 'No', 'Not sure']}
            value={form.interestedInInvestment}
            onChange={(v) => setRadio('interestedInInvestment', v)}
          />

          <RadioGroup
            label="Are you currently in any other startup programs? *"
            error={errors.inOtherPrograms}
            options={['Yes', 'No']}
            value={form.inOtherPrograms}
            onChange={(v) => setRadio('inOtherPrograms', v)}
          />

          {/* ── EMPLOYMENT & DEMOGRAPHICS ──────────────── */}
          <SectionHeader>Employment & Demographics</SectionHeader>

          <p style={styles.demoNote}>
            The following questions are for de-identified reporting purposes only and will not be used as part of your application assessment.
          </p>

          <div style={styles.row2}>
            <Field label="Total headcount *" error={errors.totalHeadcount}>
              <input type="number" min="0" step="1" style={inputStyle(errors.totalHeadcount)} value={form.totalHeadcount} onChange={set('totalHeadcount')} placeholder="5" />
            </Field>
            <Field label="Capital Region headcount *" error={errors.capitalRegionHeadcount}>
              <input type="number" min="0" step="1" style={inputStyle(errors.capitalRegionHeadcount)} value={form.capitalRegionHeadcount} onChange={set('capitalRegionHeadcount')} placeholder="3" />
            </Field>
          </div>

          <RadioGroup
            label="Is your company minority or women-owned? *"
            error={errors.isMinorityWomenOwned}
            options={['Yes', 'No']}
            value={form.isMinorityWomenOwned}
            onChange={(v) => setRadio('isMinorityWomenOwned', v)}
          />

          <RadioGroup
            label="Is the primary owner of Hispanic or Latino origin? *"
            error={errors.isHispanicLatino}
            options={['Yes', 'No', 'Prefer not to answer']}
            value={form.isHispanicLatino}
            onChange={(v) => setRadio('isHispanicLatino', v)}
          />

          <CheckboxGroup
            label="Race / ethnicity *"
            error={errors.raceEthnicity}
            options={RACE_ETHNICITY_OPTIONS}
            selected={form.raceEthnicity}
            onToggle={(v) => toggleCheckbox('raceEthnicity', v)}
          />

          <RadioGroup
            label="Is your company veteran-owned? *"
            error={errors.isVeteranOwned}
            options={['Yes', 'No']}
            value={form.isVeteranOwned}
            onChange={(v) => setRadio('isVeteranOwned', v)}
          />

          <Field label="Age range *" error={errors.ageRange}>
            <select style={inputStyle(errors.ageRange)} value={form.ageRange} onChange={set('ageRange')}>
              <option value="">Select…</option>
              {AGE_RANGES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>

          <Field label="Household income *" error={errors.householdIncome}>
            <select style={inputStyle(errors.householdIncome)} value={form.householdIncome} onChange={set('householdIncome')}>
              <option value="">Select…</option>
              {HOUSEHOLD_INCOMES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>

          {/* ── PITCH DECK ────────────────────────────── */}
          <SectionHeader>Pitch Deck (optional)</SectionHeader>

          <Field label="Upload your pitch deck">
            <input
              ref={pitchDeckRef}
              type="file"
              accept=".pdf"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                if (f && f.type !== 'application/pdf') {
                  e.target.value = '';
                  setPitchDeck(null);
                } else {
                  setPitchDeck(f);
                }
              }}
            />
            <div style={styles.pitchDeckRow}>
              <button
                type="button"
                style={styles.pitchDeckBtn}
                onClick={() => pitchDeckRef.current?.click()}
              >
                {pitchDeck ? 'Change file' : 'Choose PDF…'}
              </button>
              {pitchDeck
                ? <span style={styles.pitchDeckName}>{pitchDeck.name}</span>
                : <span style={styles.pitchDeckHint}>PDF only · max 50 MB · optional</span>
              }
              {pitchDeck && (
                <button
                  type="button"
                  style={styles.pitchDeckClear}
                  onClick={() => { setPitchDeck(null); if (pitchDeckRef.current) pitchDeckRef.current.value = ''; }}
                >
                  ✕
                </button>
              )}
            </div>
          </Field>

          {serverError && <p style={styles.serverError}>{serverError}</p>}

          <button type="submit" style={styles.submitBtn} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit application'}
          </button>
        </form>

        <p style={styles.signInLink}>
          Already have an account? <Link to="/login" style={styles.link}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}

function SectionHeader({ children }) {
  return <h2 style={styles.sectionHeader}>{children}</h2>;
}

function Field({ label, error, action, children }) {
  return (
    <div style={styles.field}>
      <div style={styles.labelRow}>
        <label style={styles.label}>{label}</label>
        {action}
      </div>
      {children}
      {error && <span style={styles.fieldError}>{error}</span>}
    </div>
  );
}

function RadioGroup({ label, error, options, value, onChange }) {
  return (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>
      <div style={styles.radioRow}>
        {options.map((opt) => (
          <label key={opt} style={styles.radioLabel}>
            <input
              type="radio"
              checked={value === opt}
              onChange={() => onChange(opt)}
              style={styles.radioInput}
            />
            <span>{opt}</span>
          </label>
        ))}
      </div>
      {error && <span style={styles.fieldError}>{error}</span>}
    </div>
  );
}

function CheckboxGroup({ label, error, options, selected, onToggle }) {
  return (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>
      <div style={styles.checkboxGrid}>
        {options.map((opt) => (
          <label key={opt} style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={selected.includes(opt)}
              onChange={() => onToggle(opt)}
              style={styles.checkboxInput}
            />
            <span>{opt}</span>
          </label>
        ))}
      </div>
      {error && <span style={styles.fieldError}>{error}</span>}
    </div>
  );
}

function inputStyle(error) {
  return {
    padding: '10px 12px',
    borderRadius: 'var(--radius-sm)',
    border: `1.5px solid ${error ? 'var(--color-overdue)' : 'var(--color-mint-dark)'}`,
    background: '#fff',
    fontFamily: 'var(--font-body)',
    fontSize: 16,
    color: 'var(--color-forest)',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  };
}

const styles = {
  page: { minHeight: '100vh', background: 'linear-gradient(to bottom, var(--color-frost) 0%, var(--color-mint-dark) 100%) fixed', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 'var(--space-8) var(--space-6)' },
  card: { background: 'var(--color-frost)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-modal)', padding: 'var(--space-10)', width: '100%', maxWidth: 860 },
  title: { fontFamily: 'var(--font-accent)', fontSize: 30, fontWeight: 800, color: 'var(--color-forest)', marginBottom: 'var(--space-2)' },
  subtitle: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.7, lineHeight: 1.6, marginBottom: 'var(--space-8)' },
  sectionHeader: { fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 'var(--space-6)', marginBottom: 'var(--space-3)', paddingBottom: 'var(--space-2)', borderBottom: '1px solid var(--color-mint-dark)' },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' },
  row3: { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 'var(--space-4)' },
  field: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 'var(--space-4)' },
  labelRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' },
  showHideBtn: { background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 600, color: 'var(--color-forest)', opacity: 0.55, padding: 0 },
  label: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 600, color: 'var(--color-forest)' },
  fieldError: { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-overdue)' },
  serverError: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-overdue)', textAlign: 'center', marginBottom: 'var(--space-4)' },
  submitBtn: { width: '100%', padding: '13px', borderRadius: 'var(--radius-sm)', background: 'var(--color-forest)', color: '#fff', fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer', marginTop: 'var(--space-4)' },
  signInLink: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', textAlign: 'center', marginTop: 'var(--space-6)', opacity: 0.75 },
  link: { color: 'var(--color-forest)', fontWeight: 700 },
  demoNote: { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', opacity: 0.6, lineHeight: 1.6, marginBottom: 'var(--space-4)', fontStyle: 'italic' },
  inlineCheck: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', opacity: 0.75, cursor: 'pointer' },
  radioRow: { display: 'flex', flexWrap: 'wrap', gap: '12px 24px' },
  radioLabel: { display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', cursor: 'pointer' },
  radioInput: { accentColor: 'var(--color-forest)', width: 16, height: 16, cursor: 'pointer', flexShrink: 0 },
  checkboxGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '8px 16px' },
  checkboxLabel: { display: 'flex', alignItems: 'flex-start', gap: 8, fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', cursor: 'pointer', lineHeight: 1.4 },
  checkboxInput: { accentColor: 'var(--color-forest)', width: 16, height: 16, cursor: 'pointer', flexShrink: 0, marginTop: 2 },
  successIcon: { width: 56, height: 56, borderRadius: 'var(--radius-full)', background: 'var(--color-status-complete-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, color: 'var(--color-forest)', margin: '0 auto var(--space-5)' },
  successTitle: { fontFamily: 'var(--font-accent)', fontSize: 28, fontWeight: 800, color: 'var(--color-forest)', textAlign: 'center', marginBottom: 'var(--space-4)' },
  successText: { fontFamily: 'var(--font-body)', fontSize: 16, color: 'var(--color-forest)', lineHeight: 1.7, textAlign: 'center', opacity: 0.8, marginBottom: 'var(--space-6)' },
  backLink: { display: 'block', textAlign: 'center', fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 600, color: 'var(--color-forest)' },
  pitchDeckRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  pitchDeckBtn: { padding: '8px 16px', borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--color-mint-dark)', background: '#fff', fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, color: 'var(--color-forest)', cursor: 'pointer', whiteSpace: 'nowrap' },
  pitchDeckName: { fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--color-forest)', fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  pitchDeckHint: { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', opacity: 0.5 },
  pitchDeckClear: { background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--color-forest)', opacity: 0.5, padding: '0 4px', flexShrink: 0 },
};
